/**
 * When does each of an EMT's credentials expire, and which reminder is due — ONE implementation for
 * the daily reminder jobs (functions/src/index.ts: checkDocumentExpiry, checkWorkforceCertExpiry),
 * the "Upcoming reminders" card and anything else that counts days to an expiry.
 *
 * It replaces three separate copies that had drifted apart:
 *  - the date: the reminder job and the card read the date the EMT TYPED, while the renewal banner
 *    read the date the document check read off the card. Here the reviewed card's date is used
 *    whenever there is one; the typed date is only the fallback for a credential with no usable check.
 *  - the day count: one copy counted from UTC midnight, one from local midnight and one rounded up
 *    from the current instant, so the same card was "14 days" on the banner and "15 days" on the
 *    card an inch below it. Here a day count is always a difference between two CALENDAR days, and
 *    the caller says which calendar ("today" where the person is, or the server's UTC day).
 *  - the reminder: it fired only when the count was EXACTLY 30, 14, 7, 1 or 0, so one missed run (or
 *    a credential added with 25 days left) skipped it for good. A reminder is now due for the whole
 *    span up to the next threshold, and is sent once per credential, expiry date and threshold.
 *
 * Credential documents uploaded under "Hireable Profile Documents" (driver's license, state license,
 * NREMT, CPR/BLS card, EMT certification) are included: they have no typed expiry at all, so the
 * date on the reviewed card is the only one there is.
 *
 * Synced verbatim into functions/src/generated/ (scripts/syncFunctionsGenerated.mjs).
 */
import { resolveCertVerification, resolveDocumentVerification } from '../credentials/certificationChecks';
import { getCategoryLabel, isCredentialDocumentCategory } from '../documents/documentCategories';
import { certificationMatchKey } from '../constants/certifications';
import { credentialDayFor, parseExpiryDate } from './certRenewal';
import { scoringZoneFor } from '../readiness/certUtils';
import { zonedDateKey } from '../scheduling/time';

/**
 * THE "today" for an EMT's credentials (2026-10-13): the EMT's own calendar day, in the zone of their
 * address state, else UTC. Re-exported from certRenewal.ts so every expiry count imports one rule.
 */
export { credentialDayFor };

const DAY_MS = 86_400_000;

/** Days before expiry at which a reminder becomes due (and 0 = the day itself). */
export const EXPIRY_REMINDER_THRESHOLDS = [30, 14, 7, 1, 0] as const;
export type ExpiryReminderBucket = (typeof EXPIRY_REMINDER_THRESHOLDS)[number] | 'expired';
/** The furthest-out reminder; also how far ahead the "Upcoming reminders" card looks. */
export const EXPIRY_REMINDER_HORIZON_DAYS = 30;

/** Which certification a credential-document category is, where it is one (so BLS is not listed twice). */
const CATEGORY_CERTIFICATION: Record<string, string> = { cprBls: 'BLS' };

export type CredentialExpiryRow = {
  /** Stable per credential: the certification's match key, or the document category. */
  key: string;
  /** What to call it: "BLS", "NREMT-B", "Driver's license". */
  name: string;
  /** YYYY-MM-DD */
  expiryDate: string;
  /** 'card' = read off the reviewed document; 'typed' = entered by the EMT (no usable check). */
  source: 'card' | 'typed';
  kind: 'certification' | 'document';
};

/**
 * Today's calendar day as YYYY-MM-DD: the device's ('local'), or UTC ('utc'). Not for an EMT's own
 * credentials: those are counted on credentialDayFor(profile), wherever the code runs.
 */
export function calendarDay(zone: 'local' | 'utc', now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return zone === 'utc'
    ? `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())}`
    : `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** A timestamp that names its zone: "2026-10-14T05:30:00Z", "...-06:00", "... GMT-0600". */
const EXPLICIT_ZONE = /(?:\dZ|[+-]\d{2}:?\d{2}|GMT[+-]\d{4})\s*(?:\([^)]*\))?$/i;
/** Exactly UTC midnight: how a date picker stores a plain calendar date as a timestamp. */
const UTC_MIDNIGHT = /T00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00)$/i;

/**
 * Any stored date → YYYY-MM-DD, or null.
 *  - Date-only strings ("2026-10-14", "10/14/2026") and UTC-midnight timestamps are taken as written
 *    (no zone shift): they are a calendar date, not an instant.
 *  - A real instant ("2026-10-14T05:30:00Z") is the day it falls on in `timeZone` (the credential
 *    holder's zone, scoringZoneFor) when one is given; without one, the date part as written, as
 *    before (it used to be converted with toISOString, the UTC day, whatever the holder's zone).
 *  - Anything else Date.parse reads without a zone ("October 14, 2026") is the date as written.
 */
export function toExpiryDay(raw: unknown, timeZone?: string | null): string | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const s = raw.trim();
  const isoInstant = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && EXPLICIT_ZONE.test(s) && !UTC_MIDNIGHT.test(s);
  if (isoInstant && timeZone) {
    const t = Date.parse(s);
    if (Number.isFinite(t)) {
      try {
        return zonedDateKey(t, timeZone);
      } catch {
        // An unknown zone: fall through to the date as written.
      }
    }
  }
  const direct = parseExpiryDate(s);
  if (direct !== null) return new Date(direct).toISOString().slice(0, 10);
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return null;
  if (EXPLICIT_ZONE.test(s)) {
    if (timeZone) {
      try {
        return zonedDateKey(t, timeZone);
      } catch {
        /* fall through */
      }
    }
    return new Date(t).toISOString().slice(0, 10);
  }
  // Parsed as the runtime's local midnight / time: its local fields are the date as written.
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Whole calendar days from `today` to `expiryDay` (both YYYY-MM-DD); negative once expired. */
export function daysUntilDay(expiryDay: string, today: string): number | null {
  const a = parseExpiryDate(expiryDay);
  const b = parseExpiryDate(today);
  return a === null || b === null ? null : Math.round((a - b) / DAY_MS);
}

/** A check result whose expiration date is the EMT's own card: passed, or rejected only for being expired. */
function cardExpiryDay(check: { status?: unknown; reasonCode?: unknown; extracted?: { expirationDate?: unknown } | null } | undefined): string | null {
  if (!check) return null;
  const passed = check.status === 'under_review' || check.status === 'verified';
  const expiredCard = check.status === 'rejected' && check.reasonCode === 'expired';
  return passed || expiredCard ? toExpiryDay(check.extracted?.expirationDate) : null;
}

/** The expiry of one "Hireable Profile Documents" entry, read off its reviewed card; null when there is none. */
export function documentCardExpiryDay(doc: unknown, checks: unknown): string | null {
  return cardExpiryDay(resolveDocumentVerification(doc, checks));
}

/**
 * One row per credential, carrying the LATEST expiry among its entries — an EMT who uploaded the
 * renewed card is not "expiring" because the old one is still on the profile.
 */
export function credentialExpiryRows(profile: unknown): CredentialExpiryRow[] {
  const p = (profile ?? {}) as { professional?: { certifications?: unknown }; certifications?: unknown; profileDocuments?: unknown; certificationChecks?: unknown };
  const byKey = new Map<string, CredentialExpiryRow>();
  const keep = (row: CredentialExpiryRow) => {
    const prev = byKey.get(row.key);
    // The card's date beats a typed one for the same credential; otherwise the later date wins.
    if (!prev || (row.source === 'card' && prev.source === 'typed') || (row.source === prev.source && row.expiryDate > prev.expiryDate)) byKey.set(row.key, row);
  };

  // A stored timestamp is read on the holder's own calendar (their address state's zone, else UTC).
  const zone = scoringZoneFor(profile);
  const certs = Array.isArray(p.professional?.certifications) ? p.professional?.certifications : Array.isArray(p.certifications) ? p.certifications : [];
  (certs as unknown[]).forEach((c, i) => {
    if (!c || typeof c !== 'object') return;
    const cert = c as { name?: unknown; title?: unknown; expiryDate?: unknown; expiresAt?: unknown };
    const name = (typeof cert.name === 'string' && cert.name.trim()) || (typeof cert.title === 'string' && cert.title.trim()) || `Certification ${i + 1}`;
    const card = cardExpiryDay(resolveCertVerification(c, p.certificationChecks));
    const typed = toExpiryDay(cert.expiryDate, zone) ?? toExpiryDay(cert.expiresAt, zone);
    const expiryDate = card ?? typed;
    if (!expiryDate) return;
    keep({ key: certificationMatchKey(name) || name.toLowerCase(), name, expiryDate, source: card ? 'card' : 'typed', kind: 'certification' });
  });

  const docs = Array.isArray(p.profileDocuments) ? p.profileDocuments : [];
  for (const d of docs as unknown[]) {
    if (!d || typeof d !== 'object') continue;
    const category = (d as { category?: unknown }).category;
    if (typeof category !== 'string' || !isCredentialDocumentCategory(category)) continue;
    const expiryDate = documentCardExpiryDay(d, p.certificationChecks);
    if (!expiryDate) continue;
    const asCert = CATEGORY_CERTIFICATION[category];
    keep({
      key: asCert ? certificationMatchKey(asCert) : `document:${category}`,
      name: asCert ?? getCategoryLabel(category),
      expiryDate,
      source: 'card',
      kind: 'document',
    });
  }
  return [...byKey.values()].sort((a, b) => a.expiryDate.localeCompare(b.expiryDate) || a.name.localeCompare(b.name));
}

/**
 * The reminder that is due at `daysUntil`: the nearest threshold at or above it, so every day from
 * 30 down to 15 is "the 30-day reminder", 14 down to 8 "the 14-day reminder", and so on. Null when
 * the expiry is further out than the first threshold.
 */
export function expiryReminderBucket(daysUntil: number): ExpiryReminderBucket | null {
  if (!Number.isFinite(daysUntil)) return null;
  if (daysUntil < 0) return 'expired';
  let due: ExpiryReminderBucket | null = null;
  for (const t of EXPIRY_REMINDER_THRESHOLDS) if (daysUntil <= t) due = t;
  return due;
}

/**
 * The reminders that come AFTER `bucket` for the same expiry (30 → 14, 7, 1, 0, expired). If one of
 * them was already sent, this one is stale and must not go out: e.g. when the day a person's
 * reminders are counted on moves back (the 2026-10-13 switch from the UTC day to the EMT's own day,
 * or an EMT moving west), "expires today" is never sent after "has expired".
 */
export function laterExpiryReminderBuckets(bucket: ExpiryReminderBucket): ExpiryReminderBucket[] {
  const order: ExpiryReminderBucket[] = [...EXPIRY_REMINDER_THRESHOLDS, 'expired'];
  const i = order.indexOf(bucket);
  return i < 0 ? [] : order.slice(i + 1);
}

function slug(value: string): string {
  return value.replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9_-]/g, '-').replace(/-+/g, '-').slice(0, 80);
}

/**
 * Notification id for one reminder. It carries the expiry date, so a renewed credential (a new date)
 * is reminded about again when ITS time comes — the old id was per credential and threshold only,
 * which silenced every later cycle.
 */
export function expiryReminderId(prefix: string, name: string, expiryDate: string, bucket: ExpiryReminderBucket): string {
  return `${prefix}-${slug(name)}-${expiryDate.replace(/-/g, '')}-${bucket === 'expired' ? 'expired' : `${bucket}d`}`.slice(0, 200);
}

/** The id this reminder had before the expiry date was part of it (still honoured, so nothing is sent twice). */
export function legacyExpiryReminderId(prefix: string, name: string, bucket: ExpiryReminderBucket): string {
  return `${prefix}-${slug(name)}-${bucket === 'expired' ? 'expired' : `${bucket}d`}`.replace(/-+/g, '-').slice(0, 120);
}

/** "BLS expires in 14 days" / "BLS expires today" / "BLS has expired" — the same words everywhere. */
export function expiryHeadline(name: string, daysUntil: number): string {
  if (daysUntil < 0) return `${name} has expired`;
  if (daysUntil === 0) return `${name} expires today`;
  return `${name} expires in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

/**
 * "2026-10-12" -> "October 12, 2026". Read as a calendar date with plain string math (no Date, no
 * time zone), so it never shifts a day on a server in UTC or a reader in another zone. Anything that
 * is not YYYY-MM-DD is returned trimmed, as given.
 */
export function formatCalendarDay(day: string): string {
  const raw = String(day ?? '').trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!m) return raw;
  const month = Number(m[2]);
  const date = Number(m[3]);
  if (month < 1 || month > 12 || date < 1 || date > 31) return raw;
  return `${MONTH_NAMES[month - 1]} ${date}, ${m[1]}`;
}
